/* ==========================================================
   ALDO - ALSO BOUGHT CUSTOMISATIONS

   1. Product title:
      Model - Subcategory - Colour

   2. Custom variant selector:
      - CBB native select remains the source of truth
      - Custom ALDO UI
      - Dropdown is mounted to <body>
      - Avoids CBB carousel overflow/clipping
   ========================================================== */

(function () {
  'use strict';

  const productCache = new Map();

  let activeSelector = null;
  let activeDropdown = null;
  let activeButton = null;


  /* ========================================================
     PRODUCT TITLE
     ======================================================== */

  function getHandle(href) {
    if (!href) return null;

    try {
      const url = new URL(
        href,
        window.location.origin
      );

      const match = url.pathname.match(
        /\/products\/([^/?#]+)/
      );

      if (!match || !match[1]) {
        return null;
      }

      return decodeURIComponent(
        match[1]
      );

    } catch (error) {
      return null;
    }
  }


  function getProductData(handle) {
    if (!handle) {
      return Promise.resolve(null);
    }

    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    const request = fetch(
      root +
        'products/' +
        encodeURIComponent(handle) +
        '?view=cbb-data',
      {
        credentials: 'same-origin'
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            'HTTP ' + response.status
          );
        }

        return response.text();
      })

      .then(function (text) {
        return JSON.parse(
          text.trim()
        );
      })

      .catch(function (error) {
        console.warn(
          '[ALDO CBB TITLE] Failed:',
          handle,
          error
        );

        productCache.delete(handle);

        return null;
      });

    productCache.set(
      handle,
      request
    );

    return request;
  }


  async function updateProduct(card) {
    if (!card) return;

    const productLink =
      card.querySelector(
        '.cbb-also-bought-product-image-link[href*="/products/"]'
      ) ||
      card.querySelector(
        '.cbb-also-bought-product-name a[href*="/products/"]'
      );

    if (!productLink) {
      return;
    }

    const handle = getHandle(
      productLink.getAttribute('href')
    );

    if (!handle) {
      return;
    }

    const titleLink =
      card.querySelector(
        '.cbb-also-bought-product-name a'
      );

    if (!titleLink) {
      return;
    }

    if (
      card.dataset.aldoCustomTitle ===
      handle
    ) {
      return;
    }

    if (
      card.dataset
        .aldoCustomTitleLoading ===
      handle
    ) {
      return;
    }

    card.dataset
      .aldoCustomTitleLoading =
      handle;

    const data =
      await getProductData(handle);

    const currentProductLink =
      card.querySelector(
        '.cbb-also-bought-product-image-link[href*="/products/"]'
      ) ||
      card.querySelector(
        '.cbb-also-bought-product-name a[href*="/products/"]'
      );

    const currentHandle =
      currentProductLink
        ? getHandle(
            currentProductLink.getAttribute(
              'href'
            )
          )
        : null;

    if (currentHandle !== handle) {
      delete card.dataset
        .aldoCustomTitleLoading;

      return;
    }

    if (
      data &&
      data.title &&
      typeof data.title === 'string'
    ) {
      const newTitle =
        data.title.trim();

      if (newTitle) {
        titleLink.textContent =
          newTitle;

        const imageLink =
          card.querySelector(
            '.cbb-also-bought-product-image-link'
          );

        if (imageLink) {
          imageLink.setAttribute(
            'aria-label',
            newTitle
          );
        }

        if (
          titleLink.hasAttribute(
            'aria-label'
          )
        ) {
          titleLink.setAttribute(
            'aria-label',
            newTitle
          );
        }

        if (
          titleLink.hasAttribute(
            'title'
          )
        ) {
          titleLink.setAttribute(
            'title',
            newTitle
          );
        }

        card.dataset
          .aldoCustomTitle =
          handle;
      }
    }

    delete card.dataset
      .aldoCustomTitleLoading;
  }


  /* ========================================================
     CLOSE ACTIVE DROPDOWN
     ======================================================== */

  function closeActiveDropdown(
    returnFocus
  ) {
    if (!activeDropdown) {
      return;
    }

    if (activeSelector) {
      activeSelector.classList.remove(
        'is-open'
      );
    }

    if (activeButton) {
      activeButton.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    const buttonToFocus =
      activeButton;

    activeDropdown.remove();

    activeDropdown = null;
    activeSelector = null;
    activeButton = null;

    if (
      returnFocus &&
      buttonToFocus &&
      document.body.contains(
        buttonToFocus
      )
    ) {
      buttonToFocus.focus();
    }
  }


  /* ========================================================
     POSITION FLOATING DROPDOWN
     ======================================================== */

  function positionActiveDropdown() {
    if (
      !activeDropdown ||
      !activeButton
    ) {
      return;
    }

    if (
      !document.body.contains(
        activeButton
      )
    ) {
      closeActiveDropdown(false);
      return;
    }

    const rect =
      activeButton.getBoundingClientRect();

    const viewportWidth =
      document.documentElement
        .clientWidth;

    const viewportHeight =
      window.innerHeight;

    const margin = 8;

    let left = rect.left;

    let width = rect.width;

    /*
     * Protect against the dropdown
     * running off the right side.
     */
    if (
      left + width >
      viewportWidth - margin
    ) {
      left =
        Math.max(
          margin,
          viewportWidth -
            width -
            margin
        );
    }

    /*
     * Default: open downward.
     */
    let top = rect.bottom - 1;

    activeDropdown.style.left =
      Math.round(left) + 'px';

    activeDropdown.style.width =
      Math.round(width) + 'px';

    activeDropdown.style.top =
      Math.round(top) + 'px';


    /*
     * Measure after initial placement.
     */
    const dropdownRect =
      activeDropdown
        .getBoundingClientRect();

    const availableBelow =
      viewportHeight -
      rect.bottom -
      margin;

    const availableAbove =
      rect.top -
      margin;


    /*
     * If there isn't enough room below
     * and there is more room above,
     * open upwards.
     */
    if (
      dropdownRect.height >
        availableBelow &&
      availableAbove >
        availableBelow
    ) {
      const maxHeight =
        Math.max(
          120,
          Math.min(
            260,
            availableAbove
          )
        );

      activeDropdown.style.maxHeight =
        Math.floor(maxHeight) +
        'px';

      const measuredHeight =
        Math.min(
          activeDropdown.scrollHeight,
          maxHeight
        );

      top =
        rect.top -
        measuredHeight +
        1;

      activeDropdown.style.top =
        Math.round(top) + 'px';

      activeDropdown.classList.add(
        'opens-up'
      );

    } else {

      const maxHeight =
        Math.max(
          120,
          Math.min(
            260,
            availableBelow
          )
        );

      activeDropdown.style.maxHeight =
        Math.floor(maxHeight) +
        'px';

      activeDropdown.classList.remove(
        'opens-up'
      );
    }
  }


  /* ========================================================
     CREATE FLOATING OPTIONS
     ======================================================== */

  function buildFloatingDropdown(
    wrapper,
    button,
    nativeSelect,
    updateButton
  ) {
    const dropdown =
      document.createElement('div');

    dropdown.className =
      'aldo-cbb-variant-dropdown aldo-cbb-variant-dropdown--floating';

    dropdown.setAttribute(
      'role',
      'listbox'
    );


    Array.from(
      nativeSelect.options
    ).forEach(function (
      nativeOption,
      index
    ) {
      const label =
        nativeOption
          .textContent
          .trim();

      if (!label) {
        return;
      }

      const option =
        document.createElement(
          'button'
        );

      option.type = 'button';

      option.className =
        'aldo-cbb-variant-option';

      option.setAttribute(
        'role',
        'option'
      );

      option.dataset.index =
        String(index);

      option.textContent =
        label;


      if (nativeOption.disabled) {
        option.disabled = true;

        option.classList.add(
          'is-disabled'
        );
      }


      if (
        index ===
        nativeSelect.selectedIndex
      ) {
        option.classList.add(
          'is-selected'
        );

        option.setAttribute(
          'aria-selected',
          'true'
        );
      } else {
        option.setAttribute(
          'aria-selected',
          'false'
        );
      }


      option.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          event.stopPropagation();

          if (
            nativeOption.disabled
          ) {
            return;
          }


          /*
           * Update the REAL CBB select.
           */
          nativeSelect.selectedIndex =
            index;


          /*
           * Notify CBB.
           */
          nativeSelect.dispatchEvent(
            new Event(
              'input',
              {
                bubbles: true
              }
            )
          );

          nativeSelect.dispatchEvent(
            new Event(
              'change',
              {
                bubbles: true
              }
            )
          );


          updateButton();

          closeActiveDropdown(
            true
          );
        }
      );


      dropdown.appendChild(
        option
      );
    });


    /* ------------------------------------------------------
       KEYBOARD
       ------------------------------------------------------ */

    dropdown.addEventListener(
      'keydown',
      function (event) {
        const options =
          Array.from(
            dropdown.querySelectorAll(
              '.aldo-cbb-variant-option:not(:disabled)'
            )
          );

        if (!options.length) {
          return;
        }

        const currentIndex =
          options.indexOf(
            document.activeElement
          );


        if (
          event.key ===
          'ArrowDown'
        ) {
          event.preventDefault();

          const nextIndex =
            currentIndex <
            options.length - 1
              ? currentIndex + 1
              : 0;

          options[
            nextIndex
          ].focus();
        }


        if (
          event.key ===
          'ArrowUp'
        ) {
          event.preventDefault();

          const previousIndex =
            currentIndex > 0
              ? currentIndex - 1
              : options.length - 1;

          options[
            previousIndex
          ].focus();
        }


        if (
          event.key ===
          'Escape'
        ) {
          event.preventDefault();

          closeActiveDropdown(
            true
          );
        }


        if (
          event.key ===
          'Tab'
        ) {
          closeActiveDropdown(
            false
          );
        }
      }
    );


    return dropdown;
  }


  /* ========================================================
     OPEN CUSTOM DROPDOWN
     ======================================================== */

  function openCustomDropdown(
    wrapper,
    button,
    nativeSelect,
    updateButton,
    focusSelected
  ) {
    closeActiveDropdown(false);

    const dropdown =
      buildFloatingDropdown(
        wrapper,
        button,
        nativeSelect,
        updateButton
      );

    /*
     * IMPORTANT:
     * Append directly to BODY.
     *
     * It is now outside CBB's carousel
     * and cannot be clipped by it.
     */
    document.body.appendChild(
      dropdown
    );

    activeSelector = wrapper;
    activeDropdown = dropdown;
    activeButton = button;

    wrapper.classList.add(
      'is-open'
    );

    button.setAttribute(
      'aria-expanded',
      'true'
    );

    positionActiveDropdown();


    if (focusSelected) {
      requestAnimationFrame(
        function () {
          if (!activeDropdown) {
            return;
          }

          const selected =
            activeDropdown.querySelector(
              '.aldo-cbb-variant-option.is-selected:not(:disabled)'
            );

          const first =
            activeDropdown.querySelector(
              '.aldo-cbb-variant-option:not(:disabled)'
            );

          const target =
            selected || first;

          if (target) {
            target.focus();
          }
        }
      );
    }
  }


  /* ========================================================
     CUSTOM VARIANT SELECTOR
     ======================================================== */

  function initCustomVariantSelector(
    card
  ) {
    if (!card) {
      return;
    }

const nativeSelect =
  card.querySelector(
    'select:not(.aldo-cbb-custom-select)'
  );

if (!nativeSelect) {
  return;
}


/* ========================================================
   CHECK IF PRODUCT ACTUALLY HAS VARIANT CHOICES

   CBB can output a <select> even for products such as
   bags/accessories that don't have a real option to choose.

   Only show our custom selector when there are at least
   TWO meaningful selectable options.
   ======================================================== */

const meaningfulOptions =
  Array.from(nativeSelect.options).filter(
    function (option) {

      const label =
        option.textContent.trim();

      /*
       * Ignore empty / placeholder options.
       */
      if (!label) {
        return false;
      }

      return true;
    }
  );


/*
 * No real choice:
 * hide CBB's unnecessary selector completely.
 */

if (meaningfulOptions.length <= 1) {

  /*
   * Hide CBB's unnecessary native selector.
   */
  nativeSelect.classList.add(
    'aldo-cbb-native-select'
  );

  nativeSelect.dataset.aldoCustomSelector =
    'no-variants';


  /*
   * Add an invisible spacer equal to the height
   * of the real ALDO variant selector.
   *
   * This keeps price + Add to bag aligned with
   * products that DO have size choices.
   */
  if (
    !card.querySelector(
      '.aldo-cbb-variant-placeholder'
    )
  ) {
    const placeholder =
      document.createElement('div');

    placeholder.className =
      'aldo-cbb-variant-placeholder';

    nativeSelect.insertAdjacentElement(
      'afterend',
      placeholder
    );
  }

  return;
}


/*
 * Product has genuine choices.
 * Build our ALDO selector.
 */

if (
  nativeSelect.dataset
    .aldoCustomSelector ===
  'true'
) {
  return;
}

    nativeSelect.dataset
      .aldoCustomSelector =
      'true';


    /* ------------------------------------------------------
       WRAPPER
       ------------------------------------------------------ */

    const wrapper =
      document.createElement('div');

    wrapper.className =
      'aldo-cbb-variant-selector';


    /* ------------------------------------------------------
       BUTTON
       ------------------------------------------------------ */

    const button =
      document.createElement(
        'button'
      );

    button.type = 'button';

    button.className =
      'aldo-cbb-variant-button';

    button.setAttribute(
      'aria-haspopup',
      'listbox'
    );

    button.setAttribute(
      'aria-expanded',
      'false'
    );


    const buttonValue =
      document.createElement(
        'span'
      );

    buttonValue.className =
      'aldo-cbb-variant-value';


    const arrow =
      document.createElement(
        'span'
      );

    arrow.className =
      'aldo-cbb-variant-arrow';

    arrow.setAttribute(
      'aria-hidden',
      'true'
    );

    arrow.innerHTML =
      '<svg viewBox="0 0 12 8" aria-hidden="true">' +
      '<path d="M1 1.5L6 6.5L11 1.5"></path>' +
      '</svg>';


    button.appendChild(
      buttonValue
    );

    button.appendChild(
      arrow
    );


    function updateButton() {
      const selected =
        nativeSelect.options[
          nativeSelect.selectedIndex
        ];

      buttonValue.textContent =
        selected
          ? selected
              .textContent
              .trim()
          : '';
    }


    /* ------------------------------------------------------
       INSERT
       ------------------------------------------------------ */

    nativeSelect.insertAdjacentElement(
      'afterend',
      wrapper
    );

    wrapper.appendChild(
      button
    );

    nativeSelect.classList.add(
      'aldo-cbb-native-select'
    );

    updateButton();


    /* ------------------------------------------------------
       BUTTON CLICK
       ------------------------------------------------------ */

    button.addEventListener(
      'click',
      function (event) {
        event.preventDefault();
        event.stopPropagation();

        if (
          activeSelector ===
            wrapper &&
          activeDropdown
        ) {
          closeActiveDropdown(
            false
          );

          return;
        }

        openCustomDropdown(
          wrapper,
          button,
          nativeSelect,
          updateButton,
          false
        );
      }
    );


    /* ------------------------------------------------------
       BUTTON KEYBOARD
       ------------------------------------------------------ */

    button.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key ===
            'Enter' ||
          event.key ===
            ' ' ||
          event.key ===
            'ArrowDown'
        ) {
          event.preventDefault();

          openCustomDropdown(
            wrapper,
            button,
            nativeSelect,
            updateButton,
            true
          );
        }
      }
    );


    /* ------------------------------------------------------
       IF CBB CHANGES ITS SELECT
       ------------------------------------------------------ */

    nativeSelect.addEventListener(
      'change',
      function () {
        updateButton();
      }
    );
  }


  /* ========================================================
     OUTSIDE CLICK
     ======================================================== */

  document.addEventListener(
    'click',
    function (event) {
      if (!activeDropdown) {
        return;
      }

      if (
        activeDropdown.contains(
          event.target
        )
      ) {
        return;
      }

      if (
        activeButton &&
        activeButton.contains(
          event.target
        )
      ) {
        return;
      }

      closeActiveDropdown(false);
    }
  );


  /* ========================================================
     ESCAPE
     ======================================================== */

  document.addEventListener(
    'keydown',
    function (event) {
      if (
        event.key ===
          'Escape' &&
        activeDropdown
      ) {
        closeActiveDropdown(
          true
        );
      }
    }
  );


  /* ========================================================
     REPOSITION ON RESIZE
     ======================================================== */

  window.addEventListener(
    'resize',
    function () {
      if (activeDropdown) {
        positionActiveDropdown();
      }
    }
  );


  /* ========================================================
     CLOSE ON PAGE SCROLL

     Safer than allowing a floating menu to
     remain detached from the CBB card.
     ======================================================== */

  window.addEventListener(
    'scroll',
    function () {
      if (activeDropdown) {
        closeActiveDropdown(
          false
        );
      }
    },
    {
      passive: true
    }
  );


  /* ========================================================
     PROCESS PRODUCTS
     ======================================================== */

  function processAllProducts() {
    const products =
      document.querySelectorAll(
        '.cbb-also-bought-product'
      );

    products.forEach(
      function (card) {
        updateProduct(card);

        initCustomVariantSelector(
          card
        );
      }
    );
  }


  /* ========================================================
     DEBOUNCE
     ======================================================== */

  let processTimer = null;

  function scheduleProcess() {
    clearTimeout(
      processTimer
    );

    processTimer =
      setTimeout(
        processAllProducts,
        100
      );
  }


  /* ========================================================
     INITIALISE
     ======================================================== */

  function init() {
    processAllProducts();

    setTimeout(
      processAllProducts,
      500
    );

    setTimeout(
      processAllProducts,
      1500
    );

    setTimeout(
      processAllProducts,
      3000
    );


    const observer =
      new MutationObserver(
        function (mutations) {
          let cbbChanged =
            false;

          for (
            const mutation
            of mutations
          ) {
            if (
              mutation.type !==
              'childList'
            ) {
              continue;
            }

            for (
              const node
              of mutation.addedNodes
            ) {
              if (
                node.nodeType !== 1
              ) {
                continue;
              }

              if (
                node.matches &&
                (
                  node.matches(
                    '.cbb-also-bought-product'
                  ) ||
                  node.matches(
                    '.cbb-also-bought-container'
                  )
                )
              ) {
                cbbChanged =
                  true;

                break;
              }

              if (
                node.querySelector &&
                (
                  node.querySelector(
                    '.cbb-also-bought-product'
                  ) ||
                  node.querySelector(
                    '.cbb-also-bought-container'
                  )
                )
              ) {
                cbbChanged =
                  true;

                break;
              }
            }

            if (cbbChanged) {
              break;
            }
          }

          if (cbbChanged) {
            scheduleProcess();
          }
        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }


  /* ========================================================
     START
     ======================================================== */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }

})();